import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Lock,
  Plus,
  Search,
  Trash2,
  RotateCcw,
  Edit,
  ChevronDown,
  ChevronUp,
  GlassWater,
  Languages,
  Loader2,
  Save,
  X,
  Coffee,
  Citrus,
  IceCreamCone,
  Blend,
  Sparkles,
  FlaskConical,
  Upload,
  FileSpreadsheet,
  ArrowRight,
  Check,
  LayoutGrid,
  List,
  Download,
  Settings,
  Copy,
  ImagePlus,
  GitBranchPlus,
  ArrowUp,
  ArrowDown,
  Clock,
  CheckCircle,
  CheckSquare,
  Square,
  Replace,
  FolderInput,
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const DEFAULT_CATEGORY_ICONS = {
  "Bubble Tea Fruits": Citrus, "Soda": GlassWater, "Latte": Coffee,
  "Recettes": FlaskConical, "Dessert Bubble Tea": IceCreamCone,
  "Frappe": Blend, "Proteine": Sparkles,
  "Ice Coffee Corner": Coffee, "Hot Coffee Corner": Coffee,
  "The": Coffee, "Tapioca": GlassWater, "Topping": FlaskConical,
};

const COLOR_POOL = [
  "bg-orange-500/10 text-orange-600 border-orange-500/20",
  "bg-cyan-500/10 text-cyan-600 border-cyan-500/20",
  "bg-amber-500/10 text-amber-600 border-amber-500/20",
  "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  "bg-pink-500/10 text-pink-600 border-pink-500/20",
  "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "bg-lime-500/10 text-lime-600 border-lime-500/20",
  "bg-indigo-500/10 text-indigo-600 border-indigo-500/20",
  "bg-red-500/10 text-red-600 border-red-500/20",
  "bg-teal-500/10 text-teal-600 border-teal-500/20",
  "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  "bg-violet-500/10 text-violet-600 border-violet-500/20",
  "bg-rose-500/10 text-rose-600 border-rose-500/20",
  "bg-fuchsia-500/10 text-fuchsia-600 border-fuchsia-500/20",
  "bg-sky-500/10 text-sky-600 border-sky-500/20",
];

const BG_POOL = [
  "bg-orange-500/5 border-orange-200",
  "bg-cyan-500/5 border-cyan-200",
  "bg-amber-500/5 border-amber-200",
  "bg-emerald-500/5 border-emerald-200",
  "bg-pink-500/5 border-pink-200",
  "bg-blue-500/5 border-blue-200",
  "bg-lime-500/5 border-lime-200",
  "bg-indigo-500/5 border-indigo-200",
  "bg-red-500/5 border-red-200",
  "bg-teal-500/5 border-teal-200",
  "bg-yellow-500/5 border-yellow-200",
  "bg-violet-500/5 border-violet-200",
  "bg-rose-500/5 border-rose-200",
  "bg-fuchsia-500/5 border-fuchsia-200",
  "bg-sky-500/5 border-sky-200",
];

function getCategoryColor(catName, index) {
  const STATIC = {
    "Bubble Tea Fruits": 0, "Soda": 1, "Latte": 2, "Recettes": 3,
    "Dessert Bubble Tea": 4, "Frappe": 5, "Proteine": 6,
    "Ice Coffee Corner": 7, "Hot Coffee Corner": 8,
    "The": 9, "Tapioca": 10, "Topping": 11,
  };
  const i = STATIC[catName] ?? index;
  return COLOR_POOL[i % COLOR_POOL.length];
}

function getCategoryBg(catName, index) {
  const STATIC = {
    "Bubble Tea Fruits": 0, "Soda": 1, "Latte": 2, "Recettes": 3,
    "Dessert Bubble Tea": 4, "Frappe": 5, "Proteine": 6,
    "Ice Coffee Corner": 7, "Hot Coffee Corner": 8,
    "The": 9, "Tapioca": 10, "Topping": 11,
  };
  const i = STATIC[catName] ?? index;
  return BG_POOL[i % BG_POOL.length];
}

function getCategoryIcon(catName) {
  return DEFAULT_CATEGORY_ICONS[catName] || GlassWater;
}

const TOOLS_OPTIONS = ["Shaker", "Pirex", "Verre", "Blender", "Cuillère topping", "Cuillère en fer", "Machine à café"];

const emptyRecipe = {
  name: '', name_thai: '', category: 'Recettes',
  ingredients: [{ name: '', quantity: '' }],
  tools: [], tea_base: '', steps: [''], notes: '',
};

/* ===== NORMALIZE INGREDIENTS INTO STANDARD COLUMNS ===== */
const STD_COLS = [
  { key: 'creamer', label: 'Creamer' },
  { key: 'sirop', label: 'Sirop' },
  { key: 'poudre', label: 'Poudre' },
  { key: 'the', label: 'Thé' },
  { key: 'lait', label: 'Lait' },
  { key: 'lait_cond', label: 'Lait condensé' },
  { key: 'sauce', label: 'Sauce' },
  { key: 'eau', label: 'Eau' },
  { key: 'miel', label: 'Miel' },
  { key: 'soda', label: 'Eau gazeuse' },
  { key: 'boba', label: 'Boba' },
  { key: 'cafe', label: 'Café' },
  { key: 'autre', label: 'Autre' },
];

function normalizeRecipe(recipe) {
  const r = {};
  for (const ing of (recipe.ingredients || [])) {
    const n = (ing.name || '').toLowerCase().trim();
    const q = ing.quantity || '';

    if (n.includes('crèmer') || n.includes('creamer') || n === 'cream cheese (fromage frais)' || n.includes('poudre de cream cheese')) {
      r.creamer = r.creamer ? `${r.creamer}\n${q}` : q;
    } else if (n.startsWith('sirop')) {
      const type = n.replace(/sirop\s*(de\s*|d')?/i, '').replace('(rouge)', '').trim();
      const line = type ? `${type}\n${q}` : q;
      r.sirop = r.sirop ? `${r.sirop}\n${line}` : line;
    } else if (n.includes('poudre')) {
      const type = n.replace(/poudre\s*(de\s*|d')?/i, '').replace('(gélifiant)', '').trim();
      const line = type ? `${type} ${q}` : q;
      r.poudre = r.poudre ? `${r.poudre}\n${line}` : line;
    } else if (n.includes('thé') || n.includes('oolong') || n.includes('feuilles')) {
      let type = n.replace(/thé\s*/i, '').replace('vert au ', '').replace('feuilles de ', '').trim();
      if (!type) type = 'thé';
      r.the = type ? `${type}\n${q}` : q;
    } else if (n === 'lait frais' || n === 'lait') {
      r.lait = q;
    } else if (n.includes('lait de coco') || n === 'lait de coco') {
      r.lait = r.lait ? `${r.lait}\ncoco ${q}` : `coco ${q}`;
    } else if (n.includes('crème fouettée') || n.includes('whipping')) {
      r.lait = r.lait ? `${r.lait}\ncrème ${q}` : `crème ${q}`;
    } else if (n.includes('lait concentré') || n.includes('lait condensé')) {
      r.lait_cond = q;
    } else if (n.includes('sauce') || (n.includes('chocolat') && !n.includes('poudre'))) {
      const type = n.replace(/sauce\s*/i, '').trim();
      r.sauce = type ? `${type}\n${q}` : q;
    } else if (n.includes('café') || n.includes('espresso')) {
      r.cafe = q;
    } else if (n === 'eau gazeuse' || n === 'soda') {
      r.soda = q;
    } else if (n.startsWith('eau') || n === 'eau potable') {
      r.eau = q;
    } else if (n === 'miel' || n === 'miel / sirop') {
      r.miel = q;
    } else if (n.includes('boba') || n.includes('perles')) {
      r.boba = q;
    } else if (n.includes('glace') || n.includes('sucre de canne') || n.includes('tapioca')) {
      r.autre = r.autre ? `${r.autre}\n${ing.name} ${q}` : `${ing.name} ${q}`;
    } else {
      r.autre = r.autre ? `${r.autre}\n${ing.name} ${q}` : `${ing.name} ${q}`;
    }
  }

  // Merge tea_base into thé column if not already there
  if (recipe.tea_base && !r.the) {
    const tb = recipe.tea_base.toLowerCase();
    let label = recipe.tea_base.replace(/Thé\s*/i, '').replace('vert au ', '').trim();
    r.the = label || recipe.tea_base;
  }

  return r;
}

function getUsedCols(catRecipes) {
  const used = new Set();
  catRecipes.forEach(recipe => {
    const norm = normalizeRecipe(recipe);
    Object.keys(norm).forEach(k => used.add(k));
  });
  return STD_COLS.filter(c => used.has(c.key));
}

/* ===== SYNTHESIS SUB-COMPONENT (inline table like BBT Excel) ===== */
function SyntheseView({ recipes, selectedCategory, categories }) {
  const grouped = {};
  recipes.forEach(r => {
    if (!grouped[r.category]) grouped[r.category] = [];
    grouped[r.category].push(r);
  });

  const categoriesToShow = selectedCategory === 'all'
    ? Object.keys(grouped).sort()
    : [selectedCategory].filter(c => grouped[c]);

  return (
    <div className="space-y-8" data-testid="synthese-view">
      {categoriesToShow.map((cat, catIdx) => {
        const catRecipes = grouped[cat] || [];
        const CatIcon = getCategoryIcon(cat);
        const cols = getUsedCols(catRecipes);

        return (
          <div key={cat} className={`rounded-xl border overflow-hidden ${getCategoryBg(cat, catIdx)}`}
            data-testid={`synthese-cat-${cat.replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 px-4 py-3 border-b">
              <CatIcon className="w-5 h-5" />
              <h3 className="text-lg font-bold">{cat}</h3>
              <Badge variant="secondary" className="ml-auto">{catRecipes.length}</Badge>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-muted/40">
                    <th className="text-left px-3 py-2 font-semibold border-r border-border/30 whitespace-nowrap sticky left-0 bg-muted/60 z-10 min-w-[150px]">Recette</th>
                    {cols.map(col => (
                      <th key={col.key} className="text-center px-2 py-2 font-medium border-r border-border/30 whitespace-nowrap min-w-[70px]">{col.label}</th>
                    ))}
                    <th className="text-center px-2 py-2 font-medium border-r border-border/30 whitespace-nowrap min-w-[70px]">Outils</th>
                    <th className="text-left px-2 py-2 font-medium whitespace-nowrap min-w-[280px]">Étapes</th>
                  </tr>
                </thead>
                <tbody>
                  {catRecipes.map((recipe, rIdx) => {
                    const norm = normalizeRecipe(recipe);
                    return (
                      <tr key={recipe.id} className={`border-t border-border/20 ${rIdx % 2 === 0 ? 'bg-background/60' : 'bg-background/30'} hover:bg-primary/5 transition-colors`}
                        data-testid={`synthese-row-${recipe.id}`}>
                        <td className="px-3 py-2 border-r border-border/30 sticky left-0 bg-inherit z-10">
                          <div className="font-semibold text-sm">{recipe.name}</div>
                          {recipe.name_thai && <div className="text-[10px] text-muted-foreground">{recipe.name_thai}</div>}
                        </td>
                        {cols.map(col => (
                          <td key={col.key} className="text-center px-2 py-2 border-r border-border/30 font-mono text-[11px]">
                            {norm[col.key]
                              ? <span className="whitespace-pre-line">{norm[col.key]}</span>
                              : <span className="text-muted-foreground/30">-</span>}
                          </td>
                        ))}
                        <td className="text-center px-2 py-2 border-r border-border/30">
                          <div className="flex flex-wrap gap-0.5 justify-center">
                            {recipe.tools?.map((t, i) => (
                              <span key={i} className="bg-muted px-1 py-0 rounded text-[10px]">{t}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex flex-wrap items-center gap-0.5">
                            {recipe.steps?.map((step, i) => (
                              <span key={i} className="flex items-center gap-0.5">
                                <span className="bg-primary/10 text-primary px-1 py-0 rounded whitespace-nowrap text-[10px]">{step}</span>
                                {i < recipe.steps.length - 1 && <ArrowRight className="w-2.5 h-2.5 text-muted-foreground shrink-0" />}
                              </span>
                            ))}
                          </div>
                          {recipe.notes && <div className="text-[10px] text-muted-foreground italic mt-1">{recipe.notes}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {categoriesToShow.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          Aucune recette dans cette catégorie
        </div>
      )}
    </div>
  );
}


/* ===== CATEGORY MANAGER DIALOG ===== */
function CategoryManager({ open, onOpenChange, api, categories, onCategoriesChanged, recipeCounts }) {
  const [newCatName, setNewCatName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    const name = newCatName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post('/recipes/categories', { name });
      toast.success(`Catégorie "${name}" créée`);
      setNewCatName('');
      onCategoriesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleRename = async (catId) => {
    const name = editingName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.put(`/recipes/categories/${catId}`, { name });
      toast.success('Catégorie renommée');
      setEditingId(null);
      onCategoriesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Supprimer la catégorie "${cat.name}" ?`)) return;
    try {
      await api.delete(`/recipes/categories/${cat.id}`);
      toast.success(`Catégorie "${cat.name}" supprimée`);
      onCategoriesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle data-testid="category-manager-title">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Gérer les catégories
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 mb-3">
          <Input
            placeholder="Nouvelle catégorie..."
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            data-testid="new-category-input"
          />
          <Button onClick={handleAdd} disabled={saving || !newCatName.trim()} data-testid="add-category-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="space-y-1 pr-2">
            {categories.map((cat, idx) => {
              const count = recipeCounts[cat.name] || 0;
              const isEditing = editingId === cat.id;
              return (
                <div key={cat.id} className="flex items-center gap-2 p-2 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors"
                  data-testid={`category-item-${cat.id}`}>
                  <div className={`w-3 h-3 rounded-full ${getCategoryColor(cat.name, idx).split(' ')[0]}`} />
                  {isEditing ? (
                    <Input
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRename(cat.id); if (e.key === 'Escape') setEditingId(null); }}
                      className="h-7 text-sm flex-1"
                      autoFocus
                      data-testid={`rename-category-input-${cat.id}`}
                    />
                  ) : (
                    <span className="flex-1 text-sm font-medium">{cat.name}</span>
                  )}
                  {count > 0 && <Badge variant="secondary" className="text-xs">{count}</Badge>}
                  <div className="flex gap-0.5">
                    {isEditing ? (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRename(cat.id)} disabled={saving}
                          data-testid={`save-rename-${cat.id}`}>
                          <Check className="w-3.5 h-3.5 text-green-600" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingId(null)}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => { setEditingId(cat.id); setEditingName(cat.name); }}
                          data-testid={`edit-category-${cat.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                          onClick={() => handleDelete(cat)} disabled={count > 0}
                          title={count > 0 ? `${count} recette(s) utilisent cette catégorie` : 'Supprimer'}
                          data-testid={`delete-category-${cat.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter className="pt-3 border-t">
          <p className="text-xs text-muted-foreground flex-1">
            {categories.length} catégories
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ===== IMPORT DIALOG ===== */
function ImportDialog({ open, onOpenChange, api, onSuccess }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setResult(null); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/recipes/import-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setResult(res.data);
      toast.success(res.data.message || `${res.data.count} recettes importées`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle data-testid="import-dialog-title">
            <div className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Importer des recettes
            </div>
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="flex flex-col items-center py-8 gap-4">
            <CheckCircle className="w-16 h-16 text-green-500" />
            <p className="text-lg font-semibold">{result.count} recettes importées</p>
            <p className="text-sm text-muted-foreground text-center">
              Les recettes sont dans l'onglet <span className="font-medium text-amber-600">"En attente"</span> pour vérification avant validation.
            </p>
            <Button onClick={() => { reset(); onOpenChange(false); }} data-testid="import-done-btn">
              Voir les recettes en attente
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            {uploading ? (
              <>
                <Loader2 className="w-12 h-12 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Extraction et traduction en cours...</p>
                <p className="text-xs text-muted-foreground">Cela peut prendre 1-2 minutes pour les gros fichiers</p>
              </>
            ) : (
              <>
                <div className="border-2 border-dashed border-muted-foreground/20 rounded-xl p-8 w-full text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => fileRef.current?.click()}>
                  <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="font-medium">Cliquez pour choisir un fichier</p>
                  <p className="text-sm text-muted-foreground mt-1">Excel (.xlsx), CSV (.csv), PDF (.pdf) ou Word (.docx)</p>
                </div>
                <input ref={fileRef} type="file" className="hidden" onChange={handleUpload}
                  accept=".xlsx,.xls,.csv,.pdf,.docx,.doc" data-testid="import-file-input" />
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ===== MAIN PAGE ===== */
export function RecettesSecretesPage() {
  const { api, isAdmin } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedRecipe, setExpandedRecipe] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [formData, setFormData] = useState({ ...emptyRecipe });
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [variantDialog, setVariantDialog] = useState(null);
  const [variantName, setVariantName] = useState('');
  const [variantSyrup, setVariantSyrup] = useState('');
  const [activeTab, setActiveTab] = useState('recettes');
  const [pendingRecipes, setPendingRecipes] = useState([]);
  const [deletedRecipes, setDeletedRecipes] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportingSynthese, setExportingSynthese] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDialog, setBulkDialog] = useState(null); // { action: 'add_ingredient'|'update_ingredient'|... }
  const [bulkPayload, setBulkPayload] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);

  const categoryNames = categories.map(c => c.name);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/recipes/categories');
      setCategories(res.data);
    } catch { /* ignore */ }
  }, [api]);

  const fetchRecipes = useCallback(async () => {
    try {
      setLoading(true);
      const [approved, pending, corbeille] = await Promise.all([
        api.get('/recipes/'),
        api.get('/recipes/pending'),
        api.get('/recipes/corbeille'),
      ]);
      setRecipes(approved.data);
      setPendingRecipes(pending.data);
      setDeletedRecipes(corbeille.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Accès non autorisé à la zone secrète");
      } else {
        toast.error("Erreur de chargement des recettes");
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchCategories(); fetchRecipes(); }, [fetchCategories, fetchRecipes]);

  const handleSeed = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const res = await api.post('/recipes/seed');
      toast.success(res.data.message);
      fetchRecipes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du seeding");
    } finally {
      setSeeding(false);
    }
  };

  const openNew = () => {
    setEditingRecipe(null);
    setFormData({ ...emptyRecipe, ingredients: [{ name: '', quantity: '' }], steps: [''], tools: [] });
    setShowEditor(true);
  };

  const openEdit = (recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      name: recipe.name || '', name_thai: recipe.name_thai || '',
      category: recipe.category || 'Recettes',
      ingredients: recipe.ingredients?.length ? [...recipe.ingredients] : [{ name: '', quantity: '' }],
      tools: recipe.tools || [], tea_base: recipe.tea_base || '',
      steps: recipe.steps?.length ? [...recipe.steps] : [''],
      notes: recipe.notes || '',
      photo_url: recipe.photo_url || '',
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const cleanData = {
        ...formData,
        ingredients: formData.ingredients.filter(i => i.name.trim()),
        steps: formData.steps.filter(s => s.trim()),
      };
      if (editingRecipe) {
        await api.put(`/recipes/${editingRecipe.id}`, cleanData);
        toast.success("Recette mise à jour");
      } else {
        await api.post('/recipes/', cleanData);
        toast.success("Recette créée");
      }
      setShowEditor(false);
      fetchRecipes();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (recipeId) => {
    if (!window.confirm("Envoyer cette recette dans la corbeille ? (elle pourra etre restauree)")) return;
    try {
      await api.delete(`/recipes/${recipeId}`);
      toast.success("Recette envoyee dans la corbeille");
      fetchRecipes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleDuplicate = async (recipeId) => {
    try {
      const res = await api.post(`/recipes/${recipeId}/duplicate`);
      toast.success(`Recette "${res.data.name}" créée`);
      fetchRecipes();
    } catch (err) { toast.error("Erreur lors de la duplication"); }
  };

  const handlePhotoUpload = async (recipeId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/recipes/${recipeId}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success("Photo ajoutée");
      fetchRecipes();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'upload"); }
  };

  const handlePhotoDelete = async (recipeId) => {
    if (!window.confirm("Supprimer la photo ?")) return;
    try {
      await api.delete(`/recipes/${recipeId}/photo`);
      toast.success("Photo supprimée");
      fetchRecipes();
    } catch (err) { toast.error("Erreur"); }
  };

  const openVariantDialog = (recipe) => {
    const syrup = recipe.ingredients?.find(i => i.name?.toLowerCase().includes('sirop'));
    setVariantDialog(recipe);
    setVariantName('');
    setVariantSyrup(syrup ? syrup.name : '');
  };

  const handleCreateVariant = async () => {
    if (!variantName.trim() || !variantDialog) return;
    try {
      const res = await api.post(`/recipes/${variantDialog.id}/variant`, {
        name: variantName.trim(),
        syrup: variantSyrup.trim()
      });
      toast.success(`Variante "${res.data.name}" créée`);
      setVariantDialog(null);
      fetchRecipes();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleApproveOne = async (recipeId) => {
    try {
      await api.post(`/recipes/pending/${recipeId}/approve`);
      toast.success("Recette validée");
      fetchRecipes();
    } catch (err) { toast.error("Erreur"); }
  };

  const handleMoveToPending = async (recipeId) => {
    try {
      await api.post(`/recipes/${recipeId}/move-to-pending`);
      toast.success("Recette déplacée en attente");
      fetchRecipes();
      setActiveTab('pending');
    } catch (err) { toast.error("Erreur"); }
  };

  const handleApproveAll = async () => {
    if (!window.confirm(`Valider les ${pendingRecipes.length} recettes en attente ?`)) return;
    try {
      const res = await api.post('/recipes/pending/approve-all');
      toast.success(res.data.message);
      fetchRecipes();
    } catch (err) { toast.error("Erreur"); }
  };

  const handleRejectAll = async () => {
    if (!window.confirm(`Envoyer les ${pendingRecipes.length} recettes en attente dans la corbeille ? (restaurables)`)) return;
    try {
      const res = await api.delete('/recipes/pending/reject-all');
      toast.success(res.data.message);
      fetchRecipes();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleRestore = async (recipeId) => {
    try {
      await api.post(`/recipes/corbeille/${recipeId}/restore`);
      toast.success('Recette restauree');
      fetchRecipes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur restauration'); }
  };

  const handlePurge = async (recipeId) => {
    if (!window.confirm("Suppression DEFINITIVE (irreversible). Continuer ?")) return;
    try {
      await api.delete(`/recipes/corbeille/${recipeId}/purge`);
      toast.success('Recette purgee definitivement');
      fetchRecipes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur (admin requis)'); }
  };

  const handlePurgeAll = async () => {
    if (!window.confirm(`PURGE DEFINITIVE de ${deletedRecipes.length} recettes. Cette action est irreversible. Continuer ?`)) return;
    try {
      const res = await api.delete('/recipes/corbeille/purge-all');
      toast.success(res.data.message);
      fetchRecipes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur (admin requis)'); }
  };

  const handleTranslate = async (text) => {
    if (!text.trim()) return;
    setTranslating(true);
    try {
      const res = await api.post('/recipes/translate', { text });
      return res.data.translated;
    } catch (err) { toast.error("Erreur de traduction"); return null; }
    finally { setTranslating(false); }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const res = await api.get('/recipes/export-pdf', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `recettes_${selectedCategory !== 'all' ? selectedCategory.replace(/\s/g, '_').toLowerCase() : 'toutes'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (err) {
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExporting(false);
    }
  };

  const handleExportSynthesePdf = async () => {
    setExportingSynthese(true);
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const res = await api.get('/recipes/export-synthese-pdf', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `synthese_recettes_${selectedCategory !== 'all' ? selectedCategory.replace(/\s/g, '_').toLowerCase() : 'toutes'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Synthèse PDF téléchargée");
    } catch (err) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExportingSynthese(false);
    }
  };

  const addIngredient = () => {
    setFormData(p => ({ ...p, ingredients: [...p.ingredients, { name: '', quantity: '' }] }));
    setTimeout(() => {
      const newIdx = formData.ingredients.length;
      const el = document.querySelector(`[data-testid="ingredient-name-${newIdx}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
    }, 100);
  };
  const removeIngredient = (i) => setFormData(p => ({ ...p, ingredients: p.ingredients.filter((_, idx) => idx !== i) }));
  const updateIngredient = (i, field, val) => setFormData(p => {
    const n = [...p.ingredients]; n[i] = { ...n[i], [field]: val }; return { ...p, ingredients: n };
  });
  const addStep = () => {
    setFormData(p => ({ ...p, steps: [...p.steps, ''] }));
    setTimeout(() => {
      const newIdx = formData.steps.length;
      const el = document.querySelector(`[data-testid="step-input-${newIdx}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
    }, 100);
  };
  const removeStep = (i) => setFormData(p => ({ ...p, steps: p.steps.filter((_, idx) => idx !== i) }));
  const updateStep = (i, val) => setFormData(p => {
    const n = [...p.steps]; n[i] = val; return { ...p, steps: n };
  });
  const moveStep = (i, dir) => setFormData(p => {
    const n = [...p.steps]; const j = i + dir;
    if (j < 0 || j >= n.length) return p;
    [n[i], n[j]] = [n[j], n[i]];
    return { ...p, steps: n };
  });
  const toggleTool = (tool) => setFormData(p => ({
    ...p, tools: p.tools.includes(tool) ? p.tools.filter(t => t !== tool) : [...p.tools, tool]
  }));

  const filteredRecipes = recipes.filter(r => {
    const matchSearch = !searchTerm ||
      r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.name_thai?.includes(searchTerm);
    const matchCat = selectedCategory === 'all' || r.category === selectedCategory;
    return matchSearch && matchCat;
  });

  const categoryCounts = {};
  recipes.forEach(r => { categoryCounts[r.category] = (categoryCounts[r.category] || 0) + 1; });

  /* === Bulk selection === */
  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const isAllFilteredSelected = filteredRecipes.length > 0 && filteredRecipes.every(r => selectedIds.has(r.id));
  const toggleSelectAllFiltered = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (isAllFilteredSelected) {
        filteredRecipes.forEach(r => next.delete(r.id));
      } else {
        filteredRecipes.forEach(r => next.add(r.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const openBulkDialog = (action) => {
    if (action === 'soft_delete' && !isAdmin) {
      toast.error('Suppression réservée aux administrateurs');
      return;
    }
    setBulkDialog({ action });
    setBulkPayload({});
  };

  const handleBulkSubmit = async () => {
    if (!bulkDialog) return;
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error('Aucune recette sélectionnée'); return; }
    setBulkLoading(true);
    try {
      const res = await api.post('/recipes/bulk', {
        recipe_ids: ids,
        action: bulkDialog.action,
        payload: bulkPayload,
      });
      const { affected, skipped } = res.data;
      toast.success(`${affected} recette(s) mise(s) à jour${skipped ? ` · ${skipped} ignorée(s)` : ''}`);
      setBulkDialog(null);
      setBulkPayload({});
      clearSelection();
      fetchRecipes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur action groupée');
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="recettes-secretes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-2xl font-heading font-bold">Zone Secrète - Recettes</h2>
            <p className="text-sm text-muted-foreground">{recipes.length} recettes au total</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && recipes.length === 0 && (
            <Button onClick={handleSeed} disabled={seeding} variant="outline" data-testid="seed-recipes-btn">
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Importer BBT
            </Button>
          )}
          <Button variant="outline" onClick={handleExportPdf} disabled={exporting || recipes.length === 0} data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            PDF Fiches
          </Button>
          <Button variant="outline" onClick={handleExportSynthesePdf} disabled={exportingSynthese || recipes.length === 0} data-testid="export-synthese-pdf-btn">
            {exportingSynthese ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LayoutGrid className="w-4 h-4 mr-2" />}
            PDF Synthèse
          </Button>
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-file-open-btn">
            <Upload className="w-4 h-4 mr-2" />
            Importer fichier
          </Button>
          <Button variant="outline" onClick={() => setShowCategoryManager(true)} data-testid="manage-categories-btn">
            <Settings className="w-4 h-4 mr-2" />
            Catégories
          </Button>
          <Button onClick={openNew} data-testid="add-recipe-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle recette
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <TabsList data-testid="recipe-tabs">
            <TabsTrigger value="recettes" data-testid="tab-recettes">
              <List className="w-4 h-4 mr-1" /> Recettes ({recipes.length})
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">
              <Clock className="w-4 h-4 mr-1" /> En attente
              {pendingRecipes.length > 0 && (
                <Badge variant="destructive" className="ml-1.5 h-5 px-1.5 text-xs">{pendingRecipes.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="synthese" data-testid="tab-synthese">
              <LayoutGrid className="w-4 h-4 mr-1" /> Synthèse
            </TabsTrigger>
            <TabsTrigger value="corbeille" data-testid="tab-corbeille">
              <Trash2 className="w-4 h-4 mr-1" /> Corbeille
              {deletedRecipes.length > 0 && (
                <Badge variant="outline" className="ml-1.5 h-5 px-1.5 text-xs border-amber-500 text-amber-600">{deletedRecipes.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Filters */}
          <div className="flex gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-60">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-recipes"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]" data-testid="category-filter">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes ({recipes.length})</SelectItem>
                {categoryNames.map(c => (
                  <SelectItem key={c} value={c}>{c} {categoryCounts[c] ? `(${categoryCounts[c]})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mt-3">
          <Badge variant={selectedCategory === 'all' ? 'default' : 'outline'} className="cursor-pointer"
            onClick={() => setSelectedCategory('all')} data-testid="cat-pill-all">
            Toutes ({recipes.length})
          </Badge>
          {categoryNames.filter(c => categoryCounts[c]).map((c, idx) => (
            <Badge key={c}
              variant={selectedCategory === c ? 'default' : 'outline'}
              className={`cursor-pointer ${selectedCategory !== c ? getCategoryColor(c, idx) : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === c ? 'all' : c)}
              data-testid={`cat-pill-${c.replace(/\s/g, '-')}`}>
              {c} ({categoryCounts[c]})
            </Badge>
          ))}
        </div>

        {/* RECETTES TAB */}
        <TabsContent value="recettes" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredRecipes.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <GlassWater className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-lg">Aucune recette trouvée</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Bulk actions toolbar */}
              <div className="flex items-center justify-between gap-3 mb-3 p-3 rounded-lg bg-muted/40 border">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={toggleSelectAllFiltered}
                    className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
                    data-testid="bulk-select-all">
                    {isAllFilteredSelected
                      ? <CheckSquare className="w-4 h-4 text-primary" />
                      : <Square className="w-4 h-4 text-muted-foreground" />}
                    {isAllFilteredSelected ? 'Tout désélectionner' : `Tout sélectionner (${filteredRecipes.length})`}
                  </button>
                  {selectedIds.size > 0 && (
                    <Badge variant="default" className="bg-primary text-primary-foreground" data-testid="bulk-count-badge">
                      {selectedIds.size} sélectionnée{selectedIds.size > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                {selectedIds.size > 0 && (
                  <div className="flex flex-wrap gap-2 animate-fade-in">
                    <Button size="sm" variant="outline" onClick={() => openBulkDialog('add_ingredient')} data-testid="bulk-add-ing-btn">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter ingrédient
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openBulkDialog('update_ingredient')} data-testid="bulk-update-ing-btn">
                      <Replace className="w-3.5 h-3.5 mr-1" /> Modifier ingrédient
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openBulkDialog('remove_ingredient')} data-testid="bulk-remove-ing-btn">
                      <X className="w-3.5 h-3.5 mr-1" /> Supprimer ingrédient
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openBulkDialog('change_category')} data-testid="bulk-change-cat-btn">
                      <FolderInput className="w-3.5 h-3.5 mr-1" /> Catégorie
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10"
                        onClick={() => openBulkDialog('soft_delete')} data-testid="bulk-delete-btn">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Corbeille
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear-btn">
                      <X className="w-3.5 h-3.5 mr-1" /> Annuler
                    </Button>
                  </div>
                )}
              </div>

            <div className="grid gap-3">
              {filteredRecipes.map((recipe, recipeIdx) => {
                const isExp = expandedRecipe === recipe.id;
                const CatIcon = getCategoryIcon(recipe.category);
                const catIdx = categoryNames.indexOf(recipe.category);
                return (
                  <Card key={recipe.id} className={`overflow-hidden transition-all duration-200 ${selectedIds.has(recipe.id) ? 'ring-2 ring-primary' : ''}`} data-testid={`recipe-card-${recipe.id}`}>
                    <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                      onClick={() => setExpandedRecipe(isExp ? null : recipe.id)}>
                      <div onClick={e => { e.stopPropagation(); toggleSelectOne(recipe.id); }}
                        className="shrink-0 cursor-pointer p-1 -m-1 rounded hover:bg-muted"
                        title={selectedIds.has(recipe.id) ? 'Déselectionner' : 'Sélectionner pour action groupée'}
                        data-testid={`select-recipe-${recipe.id}`}>
                        <Checkbox checked={selectedIds.has(recipe.id)} className="pointer-events-none" />
                      </div>
                      {recipe.photo_url ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${recipe.photo_url}`} alt={recipe.name}
                          className="w-10 h-10 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${getCategoryColor(recipe.category, catIdx)}`}>
                          <CatIcon className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold truncate">{recipe.name}</span>
                          {recipe.name_thai && <span className="text-xs text-muted-foreground">({recipe.name_thai})</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`text-xs ${getCategoryColor(recipe.category, catIdx)}`}>{recipe.category}</Badge>
                          {recipe.tea_base && <span className="text-xs text-muted-foreground">Base: {recipe.tea_base}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={e => { e.stopPropagation(); openVariantDialog(recipe); }} data-testid={`variant-recipe-${recipe.id}`}
                          title="Créer variante">
                          <GitBranchPlus className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={e => { e.stopPropagation(); handleDuplicate(recipe.id); }} data-testid={`duplicate-recipe-${recipe.id}`}
                          title="Dupliquer">
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={e => { e.stopPropagation(); openEdit(recipe); }} data-testid={`edit-recipe-${recipe.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600"
                          onClick={e => { e.stopPropagation(); handleMoveToPending(recipe.id); }} data-testid={`move-pending-${recipe.id}`}
                          title="Mettre en attente">
                          <Clock className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                            onClick={e => { e.stopPropagation(); handleDelete(recipe.id); }} data-testid={`delete-recipe-${recipe.id}`}
                            title="Supprimer (admin uniquement)">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        {isExp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </div>

                    {isExp && (
                      <div className="px-4 pb-4 border-t border-border/50 pt-4 space-y-4 animate-fade-in">
                        {/* Photo section */}
                        <div className="flex items-start gap-4">
                          {recipe.photo_url ? (
                            <div className="relative group">
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${recipe.photo_url}`} alt={recipe.name}
                                className="w-32 h-32 rounded-xl object-cover border" data-testid={`recipe-photo-${recipe.id}`} />
                              <Button variant="destructive" size="icon" className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => handlePhotoDelete(recipe.id)} data-testid={`delete-photo-${recipe.id}`}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : null}
                          <label className="cursor-pointer" data-testid={`upload-photo-${recipe.id}`}>
                            <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
                              onChange={e => { if (e.target.files[0]) handlePhotoUpload(recipe.id, e.target.files[0]); e.target.value = ''; }} />
                            <div className="w-32 h-32 rounded-xl border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-2 hover:border-primary/50 hover:bg-muted/20 transition-colors">
                              <ImagePlus className="w-6 h-6 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">{recipe.photo_url ? 'Changer' : 'Ajouter photo'}</span>
                            </div>
                          </label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <h4 className="font-medium text-sm mb-2 flex items-center gap-1"><FlaskConical className="w-4 h-4" /> Ingrédients</h4>
                            <div className="space-y-1">
                              {recipe.ingredients?.map((ing, i) => {
                                const label = ing.item || ing.name || '';
                                const qty = [ing.quantity, ing.unit].filter(Boolean).join(' ');
                                return (
                                  <div key={i} className="flex justify-between text-sm py-1 px-2 rounded bg-muted/30">
                                    <span>{label}</span>
                                    <span className="text-muted-foreground font-mono text-xs">{qty}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium text-sm mb-2">Étapes de préparation</h4>
                            <ol className="space-y-1">
                              {recipe.steps?.map((step, i) => (
                                <li key={i} className="text-sm flex gap-2 py-1">
                                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                                  <span>{step}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                          <div className="space-y-3">
                            {recipe.tools?.length > 0 && (
                              <div>
                                <h4 className="font-medium text-sm mb-2">Outils</h4>
                                <div className="flex flex-wrap gap-1">
                                  {recipe.tools.map((tool, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs">{tool}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {recipe.tea_base && (
                              <div>
                                <h4 className="font-medium text-sm mb-1">Base de thé</h4>
                                <p className="text-sm text-muted-foreground">{recipe.tea_base}</p>
                              </div>
                            )}
                            {recipe.notes && (
                              <div>
                                <h4 className="font-medium text-sm mb-1">Notes</h4>
                                <p className="text-sm text-muted-foreground">{recipe.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
            </>
          )}
        </TabsContent>

        {/* PENDING TAB */}
        <TabsContent value="pending" className="mt-4">
          {pendingRecipes.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="pending-empty">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">Aucune recette en attente</p>
              <p className="text-sm mt-1">Les recettes importées apparaîtront ici pour validation</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="pending-list">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{pendingRecipes.length} recette(s) en attente de validation</p>
                <div className="flex gap-2">
                  {isAdmin && (
                    <Button variant="destructive" size="sm" onClick={handleRejectAll} data-testid="reject-all-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Tout supprimer
                    </Button>
                  )}
                  <Button size="sm" onClick={handleApproveAll} data-testid="approve-all-btn">
                    <CheckCircle className="w-4 h-4 mr-1" /> Tout valider
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {pendingRecipes.map(recipe => {
                  const CatIcon = getCategoryIcon(recipe.category);
                  const catIdx = categoryNames.indexOf(recipe.category);
                  return (
                    <Card key={recipe.id} className="border-dashed border-amber-300 bg-amber-50/30" data-testid={`pending-recipe-${recipe.id}`}>
                      <div className="flex items-center gap-4 p-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${getCategoryColor(recipe.category, catIdx)}`}>
                          <CatIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-sm">{recipe.name}</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="outline" className={`text-xs ${getCategoryColor(recipe.category, catIdx)}`}>{recipe.category}</Badge>
                            <span className="text-xs text-muted-foreground">{recipe.ingredients?.length || 0} ingrédients, {recipe.steps?.length || 0} étapes</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => openEdit(recipe)} data-testid={`edit-pending-${recipe.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600"
                            onClick={() => handleApproveOne(recipe.id)} data-testid={`approve-one-${recipe.id}`}>
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                              onClick={() => handleDelete(recipe.id)} data-testid={`reject-one-${recipe.id}`}
                              title="Supprimer (admin uniquement)">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>


        {/* SYNTHESE TAB */}
        <TabsContent value="synthese" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <SyntheseView
              recipes={filteredRecipes}
              selectedCategory={selectedCategory}
              categories={categories}
            />
          )}
        </TabsContent>

        <TabsContent value="corbeille" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-48"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : deletedRecipes.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <Trash2 className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">La corbeille est vide</p>
              <p className="text-xs text-muted-foreground mt-1">Les recettes supprimees apparaitront ici et pourront etre restaurees.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              <Card className="border-amber-300 bg-amber-50/50">
                <CardContent className="py-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-amber-800">
                    <b>{deletedRecipes.length}</b> recette(s) dans la corbeille. Tu peux les restaurer ou les purger definitivement.
                  </p>
                  {isAdmin && (
                    <Button variant="destructive" size="sm" onClick={handlePurgeAll} data-testid="purge-all-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Vider la corbeille (admin)
                    </Button>
                  )}
                </CardContent>
              </Card>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {deletedRecipes.map(r => (
                  <Card key={r.id} className="border-dashed opacity-75 hover:opacity-100 transition" data-testid={`deleted-recipe-${r.id}`}>
                    <CardContent className="pt-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold truncate">{r.name}</p>
                          {r.category && <Badge variant="outline" className="text-[10px] mt-1">{r.category}</Badge>}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-0.5 border-t pt-2">
                        <p>Supprime le : {r.deleted_at ? new Date(r.deleted_at).toLocaleString('fr-FR') : '—'}</p>
                        <p>Par : {r.deleted_by || '—'}</p>
                      </div>
                      <div className="flex gap-1 pt-2">
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => handleRestore(r.id)} data-testid={`restore-${r.id}`}>
                          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaurer
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handlePurge(r.id)} title="Suppression definitive (admin)">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Import Dialog */}
      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        api={api}
        onSuccess={() => { fetchRecipes(); setActiveTab('pending'); }}
      />

      {/* Category Manager Dialog */}
      <CategoryManager
        open={showCategoryManager}
        onOpenChange={setShowCategoryManager}
        api={api}
        categories={categories}
        onCategoriesChanged={() => { fetchCategories(); fetchRecipes(); }}
        recipeCounts={categoryCounts}
      />

      {/* Bulk Action Dialog */}
      <Dialog open={!!bulkDialog} onOpenChange={v => { if (!v) { setBulkDialog(null); setBulkPayload({}); } }}>
        <DialogContent className="max-w-md" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle>
              {bulkDialog?.action === 'add_ingredient' && (<><Plus className="w-5 h-5 inline mr-1" /> Ajouter un ingrédient</>)}
              {bulkDialog?.action === 'update_ingredient' && (<><Replace className="w-5 h-5 inline mr-1" /> Modifier un ingrédient</>)}
              {bulkDialog?.action === 'remove_ingredient' && (<><X className="w-5 h-5 inline mr-1" /> Supprimer un ingrédient</>)}
              {bulkDialog?.action === 'change_category' && (<><FolderInput className="w-5 h-5 inline mr-1" /> Changer la catégorie</>)}
              {bulkDialog?.action === 'soft_delete' && (<><Trash2 className="w-5 h-5 inline mr-1 text-destructive" /> Envoyer à la corbeille</>)}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              S'applique à <b>{selectedIds.size}</b> recette{selectedIds.size > 1 ? 's' : ''} sélectionnée{selectedIds.size > 1 ? 's' : ''}.
            </p>
          </DialogHeader>

          <div className="space-y-3">
            {bulkDialog?.action === 'add_ingredient' && (
              <>
                <div>
                  <label className="text-xs font-medium">Nom de l'ingrédient *</label>
                  <Input value={bulkPayload.item || ''}
                    onChange={e => setBulkPayload(p => ({ ...p, item: e.target.value }))}
                    placeholder="Ex: Poudre de citron" data-testid="bulk-item" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium">Quantité</label>
                    <Input value={bulkPayload.quantity || ''}
                      onChange={e => setBulkPayload(p => ({ ...p, quantity: e.target.value }))}
                      placeholder="Ex: 1/2" data-testid="bulk-qty" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Unité</label>
                    <Input value={bulkPayload.unit || ''}
                      onChange={e => setBulkPayload(p => ({ ...p, unit: e.target.value }))}
                      placeholder="Ex: cuillère à café" data-testid="bulk-unit" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Les recettes ayant déjà cet ingrédient seront ignorées (pas de doublon).
                </p>
              </>
            )}

            {bulkDialog?.action === 'update_ingredient' && (
              <>
                <div>
                  <label className="text-xs font-medium">Ingrédient à modifier (nom exact) *</label>
                  <Input value={bulkPayload.target_item || ''}
                    onChange={e => setBulkPayload(p => ({ ...p, target_item: e.target.value }))}
                    placeholder="Ex: Poudre de citron" data-testid="bulk-target" />
                </div>
                <div>
                  <label className="text-xs font-medium">Nouveau nom (optionnel)</label>
                  <Input value={bulkPayload.new_item || ''}
                    onChange={e => setBulkPayload(p => ({ ...p, new_item: e.target.value }))}
                    placeholder="Laisser vide pour ne pas changer" data-testid="bulk-new-item" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium">Nouvelle quantité</label>
                    <Input value={bulkPayload.quantity || ''}
                      onChange={e => setBulkPayload(p => ({ ...p, quantity: e.target.value }))}
                      placeholder="Vide = inchangée" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Nouvelle unité</label>
                    <Input value={bulkPayload.unit || ''}
                      onChange={e => setBulkPayload(p => ({ ...p, unit: e.target.value }))}
                      placeholder="Vide = inchangée" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Les recettes sans cet ingrédient seront ignorées.
                </p>
              </>
            )}

            {bulkDialog?.action === 'remove_ingredient' && (
              <>
                <div>
                  <label className="text-xs font-medium">Ingrédient à supprimer (nom exact) *</label>
                  <Input value={bulkPayload.target_item || ''}
                    onChange={e => setBulkPayload(p => ({ ...p, target_item: e.target.value }))}
                    placeholder="Ex: Poudre de citron" data-testid="bulk-target-remove" />
                </div>
                <p className="text-xs text-muted-foreground">
                  L'ingrédient sera retiré de toutes les recettes sélectionnées qui le contiennent.
                </p>
              </>
            )}

            {bulkDialog?.action === 'change_category' && (
              <div>
                <label className="text-xs font-medium">Nouvelle catégorie *</label>
                <Select value={bulkPayload.category || ''} onValueChange={v => setBulkPayload(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="bulk-cat-select"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                  <SelectContent>
                    {categoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {bulkDialog?.action === 'soft_delete' && (
              <div className="p-3 rounded bg-destructive/10 text-sm text-destructive border border-destructive/20">
                Les recettes seront déplacées dans la corbeille. Tu pourras les restaurer depuis l'onglet Corbeille.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkDialog(null); setBulkPayload({}); }}>Annuler</Button>
            <Button onClick={handleBulkSubmit} disabled={bulkLoading} data-testid="bulk-submit">
              {bulkLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Appliquer aux {selectedIds.size} recette{selectedIds.size > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variant Dialog */}
      <Dialog open={!!variantDialog} onOpenChange={v => { if (!v) setVariantDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="variant-dialog-title">
              <div className="flex items-center gap-2">
                <GitBranchPlus className="w-5 h-5" />
                Créer une variante
              </div>
            </DialogTitle>
          </DialogHeader>
          {variantDialog && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Base : <span className="font-medium text-foreground">{variantDialog.name}</span>
              </p>
              <div>
                <label className="text-sm font-medium mb-1 block">Nom de la variante</label>
                <Input value={variantName} onChange={e => setVariantName(e.target.value)}
                  placeholder="Ex: Bubble Tea Kiwi" data-testid="variant-name-input"
                  onKeyDown={e => e.key === 'Enter' && handleCreateVariant()} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Nouveau sirop / ingrédient principal</label>
                <Input value={variantSyrup} onChange={e => setVariantSyrup(e.target.value)}
                  placeholder="Ex: Sirop de Kiwi" data-testid="variant-syrup-input"
                  onKeyDown={e => e.key === 'Enter' && handleCreateVariant()} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVariantDialog(null)}>Annuler</Button>
            <Button onClick={handleCreateVariant} disabled={!variantName.trim()} data-testid="create-variant-btn">
              <GitBranchPlus className="w-4 h-4 mr-2" />
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipe Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle data-testid="editor-title">
              {editingRecipe ? 'Modifier la recette' : 'Nouvelle recette'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(90vh - 140px)' }}>
            <div className="space-y-5 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Nom (français)</label>
                  <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Ex: Thé vert au jasmin" data-testid="recipe-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Nom (thaï / anglais)</label>
                  <div className="flex gap-1">
                    <Input value={formData.name_thai} onChange={e => setFormData(p => ({ ...p, name_thai: e.target.value }))}
                      placeholder="ชาเขียวมะลิ" data-testid="recipe-name-thai-input" />
                    <Button variant="outline" size="icon" className="shrink-0"
                      disabled={translating || !formData.name_thai}
                      onClick={async () => { const t = await handleTranslate(formData.name_thai); if (t) setFormData(p => ({ ...p, name: t })); }}
                      title="Traduire en français" data-testid="translate-name-btn">
                      {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Catégorie</label>
                  <Select value={formData.category} onValueChange={v => setFormData(p => ({ ...p, category: v }))}>
                    <SelectTrigger data-testid="recipe-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{categoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Base de thé</label>
                  <Input value={formData.tea_base} onChange={e => setFormData(p => ({ ...p, tea_base: e.target.value }))}
                    placeholder="Ex: Thé vert au jasmin" data-testid="recipe-tea-base-input" />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Outils</label>
                <div className="flex flex-wrap gap-2">
                  {TOOLS_OPTIONS.map(tool => (
                    <Badge key={tool} variant={formData.tools.includes(tool) ? "default" : "outline"}
                      className="cursor-pointer select-none" onClick={() => toggleTool(tool)}
                      data-testid={`tool-${tool.replace(/\s/g, '-')}`}>{tool}</Badge>
                  ))}
                  {formData.tools.filter(t => !TOOLS_OPTIONS.includes(t)).map(tool => (
                    <Badge key={tool} variant="default" className="cursor-pointer select-none gap-1"
                      onClick={() => toggleTool(tool)} data-testid={`tool-custom-${tool.replace(/\s/g, '-')}`}>
                      {tool} <X className="w-3 h-3" />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input placeholder="Ajouter un outil..." data-testid="custom-tool-input"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const v = e.target.value.trim();
                        if (!formData.tools.includes(v)) setFormData(p => ({ ...p, tools: [...p.tools, v] }));
                        e.target.value = '';
                      }
                    }} className="h-8 text-sm" />
                </div>
              </div>

              <Separator />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Ingrédients</label>
                  <Button variant="outline" size="sm" onClick={addIngredient} data-testid="add-ingredient-btn">
                    <Plus className="w-3 h-3 mr-1" /> Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.ingredients.map((ing, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input placeholder="Ingrédient" value={ing.name} onChange={e => updateIngredient(i, 'name', e.target.value)}
                        className="flex-1" data-testid={`ingredient-name-${i}`} />
                      <Input placeholder="Quantité" value={ing.quantity} onChange={e => updateIngredient(i, 'quantity', e.target.value)}
                        className="w-32" data-testid={`ingredient-qty-${i}`} />
                      {formData.ingredients.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive"
                          onClick={() => removeIngredient(i)}><X className="w-3 h-3" /></Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Étapes de préparation</label>
                  <Button variant="outline" size="sm" onClick={addStep} data-testid="add-step-btn">
                    <Plus className="w-3 h-3 mr-1" /> Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.steps.map((step, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <div className="flex flex-col shrink-0">
                        <Button variant="ghost" size="icon" className="h-5 w-5" disabled={i === 0}
                          onClick={() => moveStep(i, -1)} data-testid={`step-up-${i}`}>
                          <ArrowUp className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-5 w-5" disabled={i === formData.steps.length - 1}
                          onClick={() => moveStep(i, 1)} data-testid={`step-down-${i}`}>
                          <ArrowDown className="w-3 h-3" />
                        </Button>
                      </div>
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                      <Input placeholder={`Étape ${i + 1}`} value={step} onChange={e => updateStep(i, e.target.value)}
                        className="flex-1" data-testid={`step-input-${i}`} />
                      {formData.steps.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive"
                          onClick={() => removeStep(i)}><X className="w-3 h-3" /></Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div>
                <label className="text-sm font-medium mb-1 block">Notes</label>
                <Input value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Notes supplémentaires..." data-testid="recipe-notes-input" />
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setShowEditor(false)} data-testid="cancel-recipe-btn">Annuler</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-recipe-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingRecipe ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
